import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, GeoJSON, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { SafeMap } from '@/components/ui/SafeMap';
import L from 'leaflet';
import {
  Trees,
  Ruler,
  Map,
  Camera,
  ChevronDown,
  ChevronRight,
  Filter,
  PanelLeftClose,
} from 'lucide-react';
import type { PhotoRecordDto } from '@forest/contracts';
import { POLYGON_STATUS_LABELS } from '@forest/contracts';
import { MapLayerControl } from '@/components/map/MapLayerControl';
import { MapLegend } from '@/components/map/MapLegend';
import { useMapStyle } from '@/components/map/mapColors';
import { MapFullscreenControl } from '@/components/map/MapFullscreenControl';
import { useFullscreen } from '@/components/map/useFullscreen';
import { PhotoPreviewPanel } from '@/features/photos/PhotoPreviewPanel';
import { ParcelPhotosPanel } from '@/features/photos/ParcelPhotosPanel';
import { IcasLoadingIndicator } from '@/features/icas';
import { ParcelVectorTilesLayer } from '@/features/parcels/ParcelVectorTilesLayer';
import { EntitateClusterBubbles } from '@/features/parcels/EntitateClusterBubbles';
import '@/components/map/UcfTooltip.css';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import {
  FILTER_PANEL_ID,
  FilterToggleButton,
  PageActions,
  PageTitle,
  PageToolbar,
  StatStrip,
  useFilterPanel,
} from '@/components/ui/PageToolbar';

/** id linking the desktop toolbar toggle to the collapsible sidebar. */
const FILTER_SIDEBAR_ID = 'filter-sidebar';
/** id of that toolbar toggle, so the in-panel close button can return focus. */
const FILTER_SIDEBAR_TOGGLE_ID = 'filter-sidebar-toggle';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useParcelFacets, useParcels } from '@/features/parcels/useParcels';
import { usePhotos, usePhotoFacets } from '@/features/photos/usePhotos';
import markerIconUrl from 'leaflet/dist/images/marker-icon.png?url';
import markerIcon2xUrl from 'leaflet/dist/images/marker-icon-2x.png?url';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png?url';

L.Icon.Default.mergeOptions({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIcon2xUrl,
  shadowUrl: markerShadowUrl,
});

const MOLDOVA_CENTER: [number, number] = [47.0105, 28.8638];

const POLYGON_STATUSES = ['IN_POLYGON', 'AUTO_ADJUSTED', 'NOT_ASSIGNED', 'MANUAL_ADJUSTED'] as const;

// Parcel polygons now flow through `<ParcelVectorTilesLayer>` (MVT tiles
// from /api/parcels/tiles). The PARCEL_CANVAS + UcfLabelDeclutter
// helpers that previously gated SVG rendering are gone — vector tiles
// render server-side and the browser only paints the viewport.

/**
 * Dedicated Leaflet pane for photo markers. z-index 550 sits ABOVE
 * the parcel MVT polygon pane (parcelTilePane, 450) and below the
 * default markerPane (600), so photo dots paint on top of polygons
 * without us having to override Leaflet's own marker layering. The
 * pane is created lazily inside the map by <PhotoPaneSetup>.
 *
 * Before this, photos shared the default `overlayPane` (400) with
 * generic overlays — the parcel layer's 450 pane covered them,
 * making photos unselectable wherever a polygon was painted.
 */
const PHOTO_PANE = 'photoMarkerPane';

function ensurePhotoPane(map: L.Map): HTMLElement {
  const existing = map.getPane(PHOTO_PANE);
  if (existing) return existing;
  const pane = map.createPane(PHOTO_PANE);
  pane.style.zIndex = '550';
  return pane;
}

/**
 * Shared canvas renderer for all photo markers. With ~500 photos visible
 * after a broad filter, the default SVG renderer creates one DOM path
 * per marker — pan/zoom then becomes the bottleneck because each path
 * gets re-laid-out by Leaflet's projection code. A single canvas
 * renderer paints them all in one pass per frame, which is the
 * difference between "freezing" and "smooth" on the country-zoom view.
 *
 * Pinned to `photoMarkerPane` so the canvas element ends up in the
 * higher-z-index pane created by <PhotoPaneSetup>.
 */
const PHOTO_CANVAS =
  typeof window !== 'undefined'
    ? L.canvas({ padding: 0.5, pane: PHOTO_PANE })
    : undefined;

/**
 * Tiny bridge component that guarantees `photoMarkerPane` exists on
 * the map BEFORE any <PhotoMarker> mounts and triggers the shared
 * PHOTO_CANVAS renderer to attach. Returns null — render purely for
 * its useLayoutEffect side-effect.
 */
function PhotoPaneSetup() {
  const map = useMap();
  useLayoutEffect(() => {
    if (map) ensurePhotoPane(map);
  }, [map]);
  return null;
}

/**
 * Zoom level at which the country-zoom cluster bubbles hand off to the
 * detailed polygon + photo layers. Below this, only bubbles render;
 * at or above, the real MVT polygons and individual photo markers
 * paint. 9 was chosen empirically — Moldova fits in ~z7, raions at
 * ~z9, individual parcels become legibly sized at ~z11.
 */
const CLUSTER_THRESHOLD_ZOOM = 9;

/**
 * Zoom at which the entity bubbles finally give way. The map opens at
 * z8, so this keeps them through three "+" clicks (9, 10, 11) — long
 * enough to still see which entity you are inside while the polygons
 * are already drawn. Deliberately NOT the same as
 * CLUSTER_THRESHOLD_ZOOM: the detail layers still come in at 9.
 */
const BUBBLE_HIDE_ZOOM = 12;

/** Renders children only when the current map zoom is at or above the
 * threshold. Used to gate the photo markers — at country zoom the
 * cluster bubbles take their place. */
function ZoomGated({
  minZoom,
  children,
}: {
  minZoom: number;
  children: React.ReactNode;
}) {
  const map = useMap();
  const [zoom, setZoom] = useState<number>(() => map.getZoom());
  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => {
      map.off('zoomend', onZoom);
    };
  }, [map]);
  if (zoom < minZoom) return null;
  return <>{children}</>;
}

/**
 * Keeps Leaflet's internal size in sync with its container. Without this,
 * resizing the window or toggling the filter sheet leaves the map with
 * grey/half-painted tiles until the next pan. A ResizeObserver on the map
 * container triggers `invalidateSize` (debounced via rAF).
 */
/**
 * Reports the visible bounds as `minLon,minLat,maxLon,maxLat`.
 *
 * The photo layer draws one marker per fetched row, so the fetch has to
 * follow the viewport — otherwise the cap silently samples the whole
 * country instead of showing what is on screen. Padded by 20% so a small
 * pan reuses the same query key instead of refetching, and rounded to 3
 * decimals (~100 m) for the same reason.
 */
function MapBoundsTracker({ onChange }: { onChange: (bbox: string) => void }) {
  const map = useMap();
  useEffect(() => {
    const report = () => {
      const b = map.getBounds().pad(0.2);
      const r = (n: number) => Math.round(n * 1000) / 1000;
      onChange(
        [r(b.getWest()), r(b.getSouth()), r(b.getEast()), r(b.getNorth())].join(','),
      );
    };
    report();
    map.on('moveend', report);
    map.on('zoomend', report);
    return () => {
      map.off('moveend', report);
      map.off('zoomend', report);
    };
  }, [map, onChange]);
  return null;
}

function AutoInvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => map.invalidateSize());
    });
    ro.observe(el);
    // One deferred pass after mount (covers the initial layout settle).
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [map]);
  return null;
}

function toGeoJsonGeometry(
  geometryType: string | null,
  coords: unknown,
): GeoJSON.Geometry | null {
  if (!coords || !geometryType) return null;
  if (typeof coords === 'object' && !Array.isArray(coords)) {
    const obj = coords as Record<string, unknown>;
    if (typeof obj.type === 'string' && 'coordinates' in obj) {
      return obj as unknown as GeoJSON.Geometry;
    }
  }
  return {
    type: geometryType as GeoJSON.Geometry['type'],
    coordinates: coords as unknown as number[],
  } as GeoJSON.Geometry;
}

/* ------------------------------------------------------------------ */
/*  Filter state                                                      */
/* ------------------------------------------------------------------ */

type Filters = {
  search: string;
  // Location — multi-select
  entitatiSilvice: string[];
  raioane: string[];
  primarii: string[];
  ocoaleSilvice: string[];
  // Technical
  codProiect: string;
  ucf: string;
  suprafataMin: string;
  suprafataMax: string;
  // Period
  years: string[]; // multi-select
  anulMin: string;
  anulMax: string;
  semester: string;
  // Photos
  polygonStatuses: string[]; // multi-select
  hasParcel: string; // '' | 'true' | 'false'
  project: string; // csv of PhotoProject values
  month: string; // 1-12
  authorCods: string[]; // multi-select on the author's short `cod`
};

const EMPTY: Filters = {
  search: '',
  entitatiSilvice: [],
  raioane: [],
  primarii: [],
  ocoaleSilvice: [],
  codProiect: '',
  ucf: '',
  suprafataMin: '',
  suprafataMax: '',
  years: [],
  anulMin: '',
  anulMax: '',
  semester: '',
  polygonStatuses: [],
  hasParcel: '',
  project: '',
  month: '',
  authorCods: [],
};

const MULTI_KEYS: (keyof Filters)[] = [
  'entitatiSilvice',
  'raioane',
  'primarii',
  'ocoaleSilvice',
  'years',
  'polygonStatuses',
  'authorCods',
];

const FILTER_KEYS = Object.keys(EMPTY) as (keyof Filters)[];

function readFromSearch(sp: URLSearchParams): Filters {
  const f: Filters = { ...EMPTY };
  for (const key of FILTER_KEYS) {
    const v = sp.get(key);
    if (!v) continue;
    if ((MULTI_KEYS as string[]).includes(key)) {
      const list = v.split(',').map((s) => s.trim()).filter(Boolean);
      (f[key] as string[]) = Array.from(new Set(list));
    } else {
      (f[key] as string) = v;
    }
  }
  return f;
}

function toUrlSearchParams(f: Filters): URLSearchParams {
  const sp = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const val = f[key];
    if (Array.isArray(val)) {
      if (val.length > 0) sp.set(key, val.join(','));
    } else if (val) {
      sp.set(key, val);
    }
  }
  return sp;
}

function countActiveFilters(f: Filters): number {
  return FILTER_KEYS.filter((k) => {
    const v = f[k];
    return Array.isArray(v) ? v.length > 0 : v !== '';
  }).length;
}

/* ------------------------------------------------------------------ */
/*  Latest-filter persistence                                          */
/*                                                                     */
/*  Keep the most recent filter selection in localStorage so leaving   */
/*  the page and coming back restores it. The copy survives until the  */
/*  user hits Reset (EMPTY clears it) or 1 hour elapses. The TTL is     */
/*  sliding: re-stamped on every save/restore, mirroring the app's      */
/*  session model (CLAUDE.md — "sliding renewal"). A non-empty URL (a   */
/*  shared/deep link) always wins over the stored copy.                */
/* ------------------------------------------------------------------ */

const FILTER_STORAGE_KEY = 'forest.filter.latest';
const FILTER_TTL_MS = 60 * 60 * 1000; // 1 hour

function loadSavedFilters(): Filters | null {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { savedAt?: number; filters?: Partial<Filters> };
    if (!parsed || typeof parsed.savedAt !== 'number' || !parsed.filters) return null;
    if (Date.now() - parsed.savedAt > FILTER_TTL_MS) {
      localStorage.removeItem(FILTER_STORAGE_KEY);
      return null;
    }
    // Merge onto EMPTY so a payload written by an older build (missing a
    // key added since) still yields a complete, well-typed Filters object.
    return { ...EMPTY, ...parsed.filters };
  } catch {
    return null;
  }
}

function saveFilters(f: Filters): void {
  try {
    // No active filters → nothing worth restoring; clear any prior copy
    // (this is what makes Reset wipe the persisted selection).
    if (countActiveFilters(f) === 0) {
      localStorage.removeItem(FILTER_STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), filters: f }),
    );
  } catch {
    /* private mode / quota exceeded — persistence is best-effort */
  }
}

/* ------------------------------------------------------------------ */
/*  Collapsible section                                               */
/* ------------------------------------------------------------------ */

function FilterSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-200 last:border-b-0 dark:border-slate-700">
      <button
        type="button"
        className="flex w-full items-center justify-between px-1 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        onClick={() => setOpen((v) => !v)}
      >
        {title}
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
      </button>
      {open && <div className="space-y-2 pb-3">{children}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Compact field helpers                                              */
/* ------------------------------------------------------------------ */

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[11px] font-medium uppercase text-slate-400 dark:text-slate-500">
      {children}
    </label>
  );
}

function InlineRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

/* ------------------------------------------------------------------ */
/*  Main page                                                         */
/* ------------------------------------------------------------------ */

export function FilterPage() {
  const { t, i18n } = useTranslation(['database', 'photos']);
  const lang = (i18n.language || 'ro') as 'ro' | 'ru' | 'en';
  const [searchParams, setSearchParams] = useSearchParams();

  // Resolve the initial filter state exactly once (lazy ref init): a
  // non-empty URL (a shared/deep link) always wins; otherwise restore the
  // persisted "latest filter" from a prior visit if it hasn't expired
  // (1h TTL). See loadSavedFilters / saveFilters above.
  const initialFiltersRef = useRef<{ filters: Filters; fromStorage: boolean } | null>(
    null,
  );
  if (initialFiltersRef.current === null) {
    const fromUrl = readFromSearch(searchParams);
    if (countActiveFilters(fromUrl) > 0) {
      initialFiltersRef.current = { filters: fromUrl, fromStorage: false };
    } else {
      const saved = loadSavedFilters();
      initialFiltersRef.current =
        saved && countActiveFilters(saved) > 0
          ? { filters: saved, fromStorage: true }
          : { filters: EMPTY, fromStorage: false };
    }
  }
  const [filters, setFilters] = useState<Filters>(initialFiltersRef.current.filters);
  const firstUrlSyncRef = useRef(true);
  const [showParcels, setShowParcels] = useState(true);
  const [showPhotos, setShowPhotos] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  /** Desktop sidebar collapse. Persisted, and open by default so the page
   *  looks unchanged until the user asks for the width. */
  const { open: sidebarOpen, toggle: toggleSidebar } = useFilterPanel(
    'forest.ui.filter.sidebar',
    true,
  );
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoRecordDto | null>(null);
  const [selectedParcel, setSelectedParcel] = useState<{
    id: string;
    ucf?: string | null;
    codProiect?: string | null;
    raion?: string | null;
    primarie?: string | null;
    anul?: number | null;
  } | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  /** Viewport of the map, so photo markers cover what is on screen. */
  const [mapBbox, setMapBbox] = useState<string | undefined>(undefined);
  const onBoundsChange = useCallback((b: string) => setMapBbox(b), []);
  // The fullscreen root wraps the map AND its side panels — see useFullscreen.
  const mapShellRef = useRef<HTMLDivElement | null>(null);
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(mapShellRef);

  /**
   * Row click on the results table — focus the map on this parcel's
   * centroid (z17 = max-useful detail, deep enough that one polygon
   * fills most of the viewport) and open the photos panel for it.
   * The centroid is fetched on demand via /api/parcels/:id/centroid
   * because the list response is `lite` (no geometry).
   */
  const handleRowClick = useCallback(
    async (r: {
      id: string;
      ucf: string;
      codProiect: string | null;
      raion: string;
      primarie: string;
      anul: number;
    }) => {
      setSelectedPhoto(null);
      setSelectedParcel({
        id: r.id,
        ucf: r.ucf,
        codProiect: r.codProiect,
        raion: r.raion,
        primarie: r.primarie,
        anul: r.anul,
      });
      const m = mapRef.current;
      if (!m) return;
      try {
        const resp = await fetch(`/api/parcels/${r.id}/centroid`, {
          credentials: 'include',
        });
        if (!resp.ok) return;
        const json = (await resp.json()) as { latitude?: number; longitude?: number };
        if (typeof json.latitude !== 'number' || typeof json.longitude !== 'number') return;
        m.flyTo([json.latitude, json.longitude], 17, { duration: 0.6 });
      } catch {
        /* network blip — selection still happened, just no zoom */
      }
    },
    [],
  );

  /** Click on a photo row: fly the map to the photo's GPS and open
   *  the preview panel. Mirrors the parcel-row behaviour so the
   *  results table is the primary navigation surface. */
  const handlePhotoRowClick = useCallback((ph: PhotoRecordDto) => {
    setSelectedParcel(null);
    setSelectedPhoto(ph);
    const m = mapRef.current;
    if (m) {
      m.flyTo([ph.latitude, ph.longitude], 17, { duration: 0.6 });
    }
  }, []);

  // On mount, if we restored filters from storage (the URL was empty),
  // push them into the URL so the view stays shareable and the URL
  // remains the source of truth from here on.
  useEffect(() => {
    if (initialFiltersRef.current?.fromStorage) {
      setSearchParams(toUrlSearchParams(initialFiltersRef.current.filters), {
        replace: true,
      });
    }
    // mount-only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep filter state in sync with the URL (browser back/forward, a
  // shared link, manual edits). Skip the very first run when we restored
  // from storage — the mount effect above is pushing those filters into
  // the URL, and reading the still-empty URL here would clobber them.
  useEffect(() => {
    if (firstUrlSyncRef.current) {
      firstUrlSyncRef.current = false;
      if (initialFiltersRef.current?.fromStorage) return;
    }
    setFilters(readFromSearch(searchParams));
  }, [searchParams]);

  // Persist the latest filter selection (1h sliding TTL) so navigating
  // away and back restores it; an empty selection (Reset) clears it.
  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  const facets = useParcelFacets();
  // Authors come from /api/photos/facets which is `requireAuth +
  // photoScope`-gated. Effect: SUPER_ADMIN / CONTROLOR see every
  // author with at least one photo; ADMINISTRATOR / AJUTOR see only
  // authors with photos inside their assigned entitati silvice (or
  // their own / parent admin's uploads). No extra client-side
  // gating needed — the server already filters by role.
  const photoFacets = usePhotoFacets();

  const activeCount = useMemo(() => countActiveFilters(filters), [filters]);

  /* --- Filter shape passed to the cluster bubble layer so its
       per-entitate counts narrow alongside the rest of the page. --- */
  const bubbleFilters = useMemo(
    () => ({
      search: filters.search || undefined,
      raioane: filters.raioane.length > 0 ? filters.raioane.join(',') : undefined,
      primarii: filters.primarii.length > 0 ? filters.primarii.join(',') : undefined,
      ocoaleSilvice:
        filters.ocoaleSilvice.length > 0 ? filters.ocoaleSilvice.join(',') : undefined,
      entitatiSilvice:
        filters.entitatiSilvice.length > 0
          ? filters.entitatiSilvice.join(',')
          : undefined,
      years: filters.years.length > 0 ? filters.years.join(',') : undefined,
      anulMin: filters.anulMin ? Number(filters.anulMin) : undefined,
      anulMax: filters.anulMax ? Number(filters.anulMax) : undefined,
      codProiect: filters.codProiect || undefined,
      ucf: filters.ucf || undefined,
      suprafataMin: filters.suprafataMin ? Number(filters.suprafataMin) : undefined,
      suprafataMax: filters.suprafataMax ? Number(filters.suprafataMax) : undefined,
      // Photo-only filters count too: the bubble's photo half is built
      // by the same where-clause as the results table, so a filter that
      // touches only photos still moves the map.
      semester: filters.semester ? Number(filters.semester) : undefined,
      polygonStatuses:
        filters.polygonStatuses.length > 0
          ? filters.polygonStatuses.join(',')
          : undefined,
      hasParcel:
        filters.hasParcel === 'true'
          ? true
          : filters.hasParcel === 'false'
            ? false
            : undefined,
      project: filters.project || undefined,
      month: filters.month ? Number(filters.month) : undefined,
      authorCods:
        filters.authorCods.length > 0 ? filters.authorCods.join(',') : undefined,
    }),
    [filters],
  );

  /* --- Per-feature predicate for the MVT polygon layer.
   *
   * The MVT tiles always ship the full parcel set (per role scope),
   * so without a client-side predicate the map ignores user filters.
   * This predicate runs once per polygon per paint inside the
   * protomaps-leaflet pipeline; returning false skips both fill and
   * stroke. Properties read here MUST exist on the tile features
   * (see services/parcel-tiles.ts → buildIndex). When in doubt add
   * the field there first.
   *
   * Wrapped in useMemo with stable dependencies so we only push a
   * new function reference into <ParcelVectorTilesLayer> when filters
   * actually change.
   */
  const parcelFilterPredicate = useMemo(() => {
    const search = filters.search.trim().toLowerCase() || null;
    const raioane = filters.raioane.length > 0 ? new Set(filters.raioane) : null;
    const primarii =
      filters.primarii.length > 0 ? new Set(filters.primarii) : null;
    const ocoaleSilvice =
      filters.ocoaleSilvice.length > 0 ? new Set(filters.ocoaleSilvice) : null;
    const entitatiSilvice =
      filters.entitatiSilvice.length > 0
        ? new Set(filters.entitatiSilvice)
        : null;
    const years =
      filters.years.length > 0
        ? new Set(filters.years.map((y) => Number(y)))
        : null;
    const anulMin = filters.anulMin ? Number(filters.anulMin) : null;
    const anulMax = filters.anulMax ? Number(filters.anulMax) : null;
    const codProiect = filters.codProiect.trim().toLowerCase() || null;
    const ucf = filters.ucf.trim().toLowerCase() || null;
    const suprafataMin = filters.suprafataMin
      ? Number(filters.suprafataMin)
      : null;
    const suprafataMax = filters.suprafataMax
      ? Number(filters.suprafataMax)
      : null;

    // Fast path: no filters set → no per-feature work.
    const anyFilter =
      search !== null ||
      raioane !== null ||
      primarii !== null ||
      ocoaleSilvice !== null ||
      entitatiSilvice !== null ||
      years !== null ||
      anulMin !== null ||
      anulMax !== null ||
      codProiect !== null ||
      ucf !== null ||
      suprafataMin !== null ||
      suprafataMax !== null;
    if (!anyFilter) return undefined;

    return (props: Record<string, unknown>) => {
      const raion = (props.raion as string) ?? '';
      const primarie = (props.primarie as string) ?? '';
      const ocol = (props.ocolSilvic as string) ?? '';
      const entitate = (props.entitateSilvica as string) ?? '';
      const cod = (props.codProiect as string) ?? '';
      const ucfVal = (props.ucf as string) ?? '';
      const anul = props.anul as number | null;
      const supr = props.suprafata as number | null;

      // Multi-selects: empty CSV slot is the "null sentinel" — match
      // an empty string property too.
      if (raioane && !raioane.has(raion)) return false;
      if (primarii && !primarii.has(primarie)) return false;
      if (ocoaleSilvice && !ocoaleSilvice.has(ocol)) return false;
      if (entitatiSilvice && !entitatiSilvice.has(entitate)) return false;

      // Years multi-select / range.
      if (years && (anul == null || !years.has(anul))) return false;
      if (anulMin != null && (anul == null || anul < anulMin)) return false;
      if (anulMax != null && (anul == null || anul > anulMax)) return false;

      // Free-text contains.
      if (
        codProiect &&
        !cod.toLowerCase().includes(codProiect)
      )
        return false;
      if (ucf && !ucfVal.toLowerCase().includes(ucf)) return false;

      // Suprafata range.
      if (suprafataMin != null && (supr == null || supr < suprafataMin))
        return false;
      if (suprafataMax != null && (supr == null || supr > suprafataMax))
        return false;

      // Cross-field search box: substring match on any of the
      // visible text columns + the natural project-ucf key.
      if (search) {
        const haystack =
          `${raion} ${primarie} ${ocol} ${entitate} ${cod} ${ucfVal}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    };
  }, [
    filters.search,
    filters.raioane,
    filters.primarii,
    filters.ocoaleSilvice,
    filters.entitatiSilvice,
    filters.years,
    filters.anulMin,
    filters.anulMax,
    filters.codProiect,
    filters.ucf,
    filters.suprafataMin,
    filters.suprafataMax,
  ]);

  /* --- Build query params for parcels --- */
  const parcelParams = useMemo(
    () => ({
      page: 1,
      // The map shows EVERY live parcel via MVT tiles regardless of
      // this limit — the JSON list only feeds the stats card and
      // the results table at the bottom. 100 rows is enough for the
      // table (sticky-header + max-h-80 only ever shows ~12 anyway);
      // dropping from 500 → 100 cuts both wire time and the React
      // commit cost on initial paint by ~5×.
      limit: 100,
      // FilterPage's map runs on MVT tiles (/api/parcels/tiles); the
      // results table renders only text columns. No per-row geometry
      // needed on this surface.
      lite: true,
      search: filters.search || undefined,
      entitatiSilvice:
        filters.entitatiSilvice.length > 0 ? filters.entitatiSilvice.join(',') : undefined,
      raioane: filters.raioane.length > 0 ? filters.raioane.join(',') : undefined,
      primarii: filters.primarii.length > 0 ? filters.primarii.join(',') : undefined,
      ocoaleSilvice:
        filters.ocoaleSilvice.length > 0 ? filters.ocoaleSilvice.join(',') : undefined,
      codProiect: filters.codProiect || undefined,
      ucf: filters.ucf || undefined,
      suprafataMin: filters.suprafataMin ? Number(filters.suprafataMin) : undefined,
      suprafataMax: filters.suprafataMax ? Number(filters.suprafataMax) : undefined,
      years: filters.years.length > 0 ? filters.years.join(',') : undefined,
      anulMin: filters.anulMin ? Number(filters.anulMin) : undefined,
      anulMax: filters.anulMax ? Number(filters.anulMax) : undefined,
    }),
    [filters],
  );

  /* --- Build query params for photos --- */
  const photoParams = useMemo(
    () => ({
      page: 1,
      // Photos render as map markers (canvas-rendered via the shared
      // PHOTO_CANVAS) AND feed the results table. The cap now applies to
      // the VIEWPORT (see `bbox` below), not to the whole country, so
      // panning brings in the photos that are actually on screen instead
      // of a fixed sample of the newest 300. The canvas renderer makes
      // this many markers cheap; SVG at this count was the old freeze
      // culprit.
      limit: 500,
      bbox: mapBbox,
      search: filters.search || undefined,
      // Location filters — applied to the photo's own denormalised
      // columns server-side so the map markers + results table narrow
      // alongside polygons.
      entitatiSilvice:
        filters.entitatiSilvice.length > 0 ? filters.entitatiSilvice.join(',') : undefined,
      raioane: filters.raioane.length > 0 ? filters.raioane.join(',') : undefined,
      primarii: filters.primarii.length > 0 ? filters.primarii.join(',') : undefined,
      ocoaleSilvice:
        filters.ocoaleSilvice.length > 0 ? filters.ocoaleSilvice.join(',') : undefined,
      // Technical filters — mirror parcels: substring on the single
      // input, exact-in on a future multi-select (currently absent from
      // the photo filter UI but the API accepts CSV `codProiecte`/`ucfs`).
      codProiect: filters.codProiect || undefined,
      ucf: filters.ucf || undefined,
      suprafataMin: filters.suprafataMin ? Number(filters.suprafataMin) : undefined,
      suprafataMax: filters.suprafataMax ? Number(filters.suprafataMax) : undefined,
      years: filters.years.length > 0 ? filters.years.join(',') : undefined,
      anulMin: filters.anulMin ? Number(filters.anulMin) : undefined,
      anulMax: filters.anulMax ? Number(filters.anulMax) : undefined,
      semester: filters.semester ? Number(filters.semester) : undefined,
      polygonStatuses:
        filters.polygonStatuses.length > 0 ? filters.polygonStatuses.join(',') : undefined,
      hasParcel:
        filters.hasParcel === 'true'
          ? true
          : filters.hasParcel === 'false'
            ? false
            : undefined,
      project: filters.project || undefined,
      month: filters.month ? Number(filters.month) : undefined,
      authorCods:
        filters.authorCods.length > 0 ? filters.authorCods.join(',') : undefined,
    }),
    [filters, mapBbox],
  );

  const parcelQuery = useParcels(parcelParams);
  const photoQuery = usePhotos(photoParams);

  const parcels = parcelQuery.data?.items ?? [];
  const photos = photoQuery.data?.items ?? [];
  const totals = parcelQuery.data?.totals;

  // The list is capped at 100 / 300 rows for performance (the map
  // shows all matching features via MVT tiles regardless), so use
  // `pagination.total` for the headline counts so users see the real
  // number of matches even when the wire payload is truncated.
  const totalParcels = parcelQuery.data?.pagination.total ?? 0;
  const totalPhotos = photoQuery.data?.pagination.total ?? 0;
  /** The viewport holds more photos than one page can carry. */
  const photosTruncated = photos.length > 0 && photos.length < totalPhotos;

  // Parcel polygons no longer ship as JSON — `<ParcelVectorTilesLayer>`
  // fetches MVT tiles from /api/parcels/tiles. The list query above
  // is kept for the stats card + photo filter (parcelIds) only.
  const apply = useCallback(
    (next: Filters) => {
      setFilters(next);
      setSearchParams(toUrlSearchParams(next), { replace: true });
    },
    [setSearchParams],
  );

  const updateFilter = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) => {
      apply({ ...filters, [key]: value });
    },
    [apply, filters],
  );

  const isLoading = parcelQuery.isLoading || photoQuery.isLoading;

  /* --- Filter panel content (shared between desktop sidebar and mobile sheet) --- */
  const filterPanel = (
    <div className="space-y-1">
      {/* Active filter badge + reset */}
      <div className="flex items-center justify-between px-1 pb-2">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">{t('database:filters.title')}</span>
          {activeCount > 0 && (
            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[10px] font-bold text-white">
              {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {activeCount > 0 && (
            <button
              type="button"
              className="text-xs font-medium text-emerald-600 hover:text-emerald-700"
              onClick={() => apply(EMPTY)}
            >
              {t('database:filters.reset')}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              toggleSidebar();
              // Focus would otherwise stay on this now-clipped button.
              document.getElementById(FILTER_SIDEBAR_TOGGLE_ID)?.focus();
            }}
            title={t('database:filters.hide')}
            aria-label={t('database:filters.hide')}
            className="hidden rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 lg:inline-flex dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Section: Search */}
      <FilterSection title={t('database:filters.section.search')} defaultOpen>
        <div>
          <FieldLabel>{t('database:filters.label.search')}</FieldLabel>
          <Input
            className="h-8 text-sm"
            placeholder={t('database:filters.searchHint')}
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
          />
        </div>
      </FilterSection>

      {/* Section: Location */}
      <FilterSection title={t('database:filters.section.location')} defaultOpen>
        <div>
          <FieldLabel>{t('database:filters.label.entitateSilvica')}</FieldLabel>
          <MultiSelect
            className="text-sm"
            placeholder={t('database:filters.allEntitati')}
            countLabel={(n) => t('database:filters.entitatiSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={filters.entitatiSilvice}
            onChange={(next) => updateFilter('entitatiSilvice', next)}
            options={[
              ...(facets.data?.hasNullEntitateSilvica
                ? [{ value: '', label: t('photos:filters.unassigned') }]
                : []),
              ...(facets.data?.entitateSilvice ?? []).map((v) => ({ value: v, label: v })),
            ]}
          />
        </div>
        <div>
          <FieldLabel>{t('database:filters.label.raion')}</FieldLabel>
          <MultiSelect
            className="text-sm"
            placeholder={t('database:filters.allRaioane')}
            countLabel={(n) => t('photos:filters.raioaneSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={filters.raioane}
            onChange={(next) => updateFilter('raioane', next)}
            options={(facets.data?.raioane ?? []).map((r) => ({ value: r, label: r }))}
          />
        </div>
        <div>
          <FieldLabel>{t('database:filters.label.primarie')}</FieldLabel>
          <MultiSelect
            className="text-sm"
            placeholder={t('database:filters.allPrimarii')}
            countLabel={(n) => t('database:filters.primariiSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={filters.primarii}
            onChange={(next) => updateFilter('primarii', next)}
            options={(facets.data?.primarii ?? []).map((p) => ({ value: p, label: p }))}
          />
        </div>
        <div>
          <FieldLabel>{t('database:filters.label.ocolSilvic')}</FieldLabel>
          <MultiSelect
            className="text-sm"
            placeholder={t('database:filters.allOcoale')}
            countLabel={(n) => t('database:filters.ocoaleSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={filters.ocoaleSilvice}
            onChange={(next) => updateFilter('ocoaleSilvice', next)}
            options={[
              ...(facets.data?.hasNullOcolSilvic
                ? [{ value: '', label: t('photos:filters.unassigned') }]
                : []),
              ...(facets.data?.ocoaleSilvice ?? []).map((o) => ({ value: o, label: o })),
            ]}
          />
        </div>
      </FilterSection>

      {/* Section: Technical */}
      <FilterSection title={t('database:filters.section.technical')}>
        <div>
          <FieldLabel>{t('database:filters.label.codProiect')}</FieldLabel>
          <Input
            className="h-8 text-sm"
            placeholder={t('database:filters.label.codProiect')}
            value={filters.codProiect}
            onChange={(e) => updateFilter('codProiect', e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>{t('database:filters.label.ucf')}</FieldLabel>
          <Input
            className="h-8 text-sm"
            placeholder={t('database:filters.label.ucf')}
            value={filters.ucf}
            onChange={(e) => updateFilter('ucf', e.target.value)}
          />
        </div>
        <InlineRow>
          <div>
            <FieldLabel>{t('database:filters.label.suprafataMin')}</FieldLabel>
            <Input
              className="h-8 text-sm"
              type="number"
              min={0}
              step="0.01"
              placeholder="0"
              value={filters.suprafataMin}
              onChange={(e) => updateFilter('suprafataMin', e.target.value)}
            />
          </div>
          <div>
            <FieldLabel>{t('database:filters.label.suprafataMax')}</FieldLabel>
            <Input
              className="h-8 text-sm"
              type="number"
              min={0}
              step="0.01"
              placeholder="max"
              value={filters.suprafataMax}
              onChange={(e) => updateFilter('suprafataMax', e.target.value)}
            />
          </div>
        </InlineRow>
      </FilterSection>

      {/* Section: Period */}
      <FilterSection title={t('database:filters.section.period')} defaultOpen>
        <div>
          <FieldLabel>{t('database:filters.label.anul')}</FieldLabel>
          <MultiSelect
            className="text-sm"
            placeholder={t('database:filters.allAni')}
            countLabel={(n) => t('photos:filters.aniSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={filters.years}
            onChange={(next) => updateFilter('years', next)}
            options={(facets.data?.ani ?? []).map((a) => ({
              value: String(a),
              label: String(a),
            }))}
          />
        </div>
        <InlineRow>
          <div>
            <FieldLabel>{t('database:filters.label.anulMin')}</FieldLabel>
            <Input
              className="h-8 text-sm"
              type="number"
              min={1900}
              max={2200}
              placeholder="2020"
              value={filters.anulMin}
              onChange={(e) => updateFilter('anulMin', e.target.value)}
            />
          </div>
          <div>
            <FieldLabel>{t('database:filters.label.anulMax')}</FieldLabel>
            <Input
              className="h-8 text-sm"
              type="number"
              min={1900}
              max={2200}
              placeholder="2026"
              value={filters.anulMax}
              onChange={(e) => updateFilter('anulMax', e.target.value)}
            />
          </div>
        </InlineRow>
        <div>
          <FieldLabel>{t('database:filters.label.semester')}</FieldLabel>
          <Select
            className="h-8 text-sm"
            value={filters.semester}
            onChange={(e) => updateFilter('semester', e.target.value)}
          >
            <option value="">{t('database:filters.semester.all')}</option>
            <option value="1">{t('database:filters.semester.1')}</option>
            <option value="2">{t('database:filters.semester.2')}</option>
          </Select>
          {filters.semester && (
            <p className="mt-0.5 text-[10px] text-slate-400">
              * {t('database:filters.section.photos')} only
            </p>
          )}
        </div>
      </FilterSection>

      {/* Section: Photos */}
      <FilterSection title={t('database:filters.section.photos')}>
        <div>
          <FieldLabel>{t('database:filters.label.polygonStatus')}</FieldLabel>
          <MultiSelect
            className="text-sm"
            placeholder={t('database:filters.allStatuses')}
            countLabel={(n) => t('photos:filters.statusesSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={filters.polygonStatuses}
            onChange={(next) => updateFilter('polygonStatuses', next)}
            options={POLYGON_STATUSES.map((s) => ({
              value: s,
              label: POLYGON_STATUS_LABELS[s]?.[lang] ?? s,
            }))}
          />
        </div>
        <div>
          <FieldLabel>{t('database:filters.label.hasParcel')}</FieldLabel>
          <Select
            className="h-8 text-sm"
            value={filters.hasParcel}
            onChange={(e) => updateFilter('hasParcel', e.target.value)}
          >
            <option value="">{t('database:filters.hasParcel.all')}</option>
            <option value="true">{t('database:filters.hasParcel.yes')}</option>
            <option value="false">{t('database:filters.hasParcel.no')}</option>
          </Select>
        </div>
        <div>
          <FieldLabel>
            {t('photos:table.header.author', { defaultValue: 'Autor' })}
          </FieldLabel>
          <MultiSelect
            className="text-sm"
            placeholder={t('photos:filters.allAuthors', {
              defaultValue: 'Toți autorii',
            })}
            countLabel={(n) =>
              t('photos:filters.authorsSelected', {
                count: n,
                defaultValue: '{{count}} autori',
              })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={filters.authorCods}
            onChange={(next) => updateFilter('authorCods', next)}
            options={(photoFacets.data?.authors ?? [])
              .filter((a) => a.cod)
              .map((a) => ({
                value: a.cod as string,
                label: a.fullName
                  ? `${a.cod} — ${a.fullName}`
                  : (a.cod as string),
              }))}
          />
        </div>
      </FilterSection>

      {/* Layers */}
      <div className="border-t border-slate-200 pt-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
          {t('database:map.layers', { defaultValue: 'Layers' })}
        </p>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            checked={showParcels}
            onChange={(e) => setShowParcels(e.target.checked)}
          />
          <span className="mr-1.5 inline-block h-3 w-3 rounded-sm border border-emerald-600 bg-emerald-400/40" />
          {t('photos:filter.layerParcels')}
          <span className="ml-auto rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-emerald-700">
            {totalParcels}
          </span>
        </label>
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            checked={showPhotos}
            onChange={(e) => setShowPhotos(e.target.checked)}
          />
          <span className="inline-block h-3 w-3 rounded-full border border-blue-600 bg-blue-400/50" />
          {t('photos:filter.layerPhotos')}
          {/* Two numbers when the viewport holds more photos than one
              page: drawn / total. The badge used to show only the total,
              so the map claimed photos it was not drawing. */}
          <span
            className="ml-auto rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-blue-700"
            title={
              photosTruncated
                ? t('photos:filter.photosTruncated', {
                    shown: photos.length,
                    total: totalPhotos,
                    defaultValue:
                      'Se afișează {{shown}} din {{total}} — apropiați harta pentru a le vedea pe toate',
                  })
                : undefined
            }
          >
            {photosTruncated ? `${photos.length} / ${totalPhotos}` : totalPhotos}
          </span>
        </label>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in space-y-2">
      {/* Title + inline totals + (mobile) filter toggle, all on one line —
          every pixel here is map the user doesn't see. */}
      <PageToolbar>
        <PageTitle
          title={t('database:filter.page.title')}
          hint={t('database:filter.page.subtitle')}
        />
        <StatStrip
          items={[
            {
              label: t('database:stats.poligoane'),
              value: totalParcels.toLocaleString('ro-MD'),
              icon: Trees,
              color: 'emerald',
            },
            {
              label: t('database:stats.suprafataHa'),
              value: (totals?.totalSuprafata ?? 0).toLocaleString('ro-MD', {
                maximumFractionDigits: 2,
              }),
              icon: Ruler,
              color: 'sky',
            },
            {
              label: t('database:stats.distinctRaioane'),
              value: totals?.distinctRaioane ?? 0,
              icon: Map,
              color: 'amber',
            },
            {
              label: t('photos:stats.totalPhotos'),
              value: totalPhotos.toLocaleString('ro-MD'),
              icon: Camera,
              color: 'violet',
            },
          ]}
        />
        <PageActions>
          <FilterToggleButton
            open={mobileOpen}
            count={activeCount}
            onClick={() => setMobileOpen((v) => !v)}
            label={t('database:filters.title')}
            className="lg:hidden"
          />
          {/* Desktop twin: same control, but it collapses the sidebar
              rather than opening the mobile sheet. Exactly one of the two
              is displayed at any width. */}
          <FilterToggleButton
            open={sidebarOpen}
            count={activeCount}
            onClick={toggleSidebar}
            label={t('database:filters.title')}
            id={FILTER_SIDEBAR_TOGGLE_ID}
            controls={FILTER_SIDEBAR_ID}
            className="hidden lg:inline-flex"
          />
        </PageActions>
      </PageToolbar>

      {/* Mobile filter sheet overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setMobileOpen(false)}
          />
          <div
            id={FILTER_PANEL_ID}
            className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-xl bg-white p-4 shadow-xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-800">{t('database:filters.title')}</h2>
              <button
                type="button"
                className="text-sm font-medium text-slate-500"
                onClick={() => setMobileOpen(false)}
              >
                &times;
              </button>
            </div>
            {filterPanel}
          </div>
        </div>
      )}

      <div
        className={`grid grid-cols-1 gap-2 transition-[grid-template-columns,column-gap] duration-300 ease-in-out ${
          sidebarOpen ? 'lg:grid-cols-[280px_1fr]' : 'lg:grid-cols-[0px_1fr] lg:gap-x-0'
        }`}
      >
        {/* Desktop sidebar. The grid column animates to 0; `overflow-hidden`
            clips the 280px-wide content while it does, which reads as the
            panel sliding out to the left. */}
        <Card
          id={FILTER_SIDEBAR_ID}
          className={`hidden overflow-hidden transition-opacity duration-200 lg:block ${
            sidebarOpen ? 'opacity-100' : 'border-0 opacity-0'
          }`}
          // `inert`, not `aria-hidden`: the panel is 0-wide but still
          // display:block, so ~25 controls would stay focusable and
          // hit-testable while invisible.
          inert={!sidebarOpen}
        >
          <CardBody className="w-[280px] space-y-1">
            {filterPanel}
          </CardBody>
        </Card>

        <div className="space-y-2">
          {isLoading && !parcelQuery.data && !photoQuery.data ? (
            <div className="flex justify-center p-6">
              <Spinner />
            </div>
          ) : (
            <Card className="overflow-hidden">
              <CardBody className="p-0">
                {/* This wrapper is the fullscreen root, so the side panels
                    live inside it: in native fullscreen the browser renders
                    nothing outside this subtree. */}
                <div
                  ref={mapShellRef}
                  role="region"
                  aria-label={t('database:map.title', { count: totalParcels })}
                  className={
                    isFullscreen
                      ? // Explicit pinning, not just `h-full`: without the
                        // native API (iOS Safari) nothing else makes this
                        // fill the screen, and `height:100%` against an
                        // auto-height Card chain resolves to zero. The UA
                        // `:fullscreen` sheet applies the same box, so one
                        // class string serves both modes. z above the side
                        // panels (1100-1300), which are descendants and so
                        // still paint on top.
                        'fixed inset-0 z-[1400] h-full w-full bg-white dark:bg-slate-900'
                      : 'relative'
                  }
                >
                  <SafeMap
                    className={
                      isFullscreen ? 'h-full' : 'h-[70vh] lg:h-[calc(100vh-11rem)] lg:min-h-[420px]'
                    }
                  >
                    <MapContainer
                      ref={mapRef}
                      center={MOLDOVA_CENTER}
                      zoom={8}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom
                    >
                      <PhotoPaneSetup />
                      <AutoInvalidateSize />
                      <MapBoundsTracker onChange={onBoundsChange} />
                      <MapLayerControl />
                      {/* Must stay mounted AFTER MapLayerControl: both live in
                          Leaflet's top-right corner, which stacks by append
                          order. */}
                      <MapFullscreenControl active={isFullscreen} onToggle={toggleFullscreen} />
                      {/* Country-zoom cluster bubbles per entitate-silvica.
                          Visible below `BUBBLE_HIDE_ZOOM` (12). Click
                          a bubble to flyToBounds into its entity. */}
                      {showParcels ? (
                        <EntitateClusterBubbles
                          threshold={BUBBLE_HIDE_ZOOM}
                          filters={bubbleFilters}
                        />
                      ) : null}
                      {/* Parcel polygons via server-rendered MVT tiles.
                          Hidden below the cluster threshold (bubbles take
                          over there) and rendered as actual polygons
                          above it. */}
                      <ParcelVectorTilesLayer
                        enabled={showParcels}
                        minZoom={CLUSTER_THRESHOLD_ZOOM}
                        filterPredicate={parcelFilterPredicate}
                        onSelect={(p) => {
                          setSelectedPhoto(null);
                          setSelectedParcel(p);
                        }}
                      />
                      {showPhotos ? (
                        // Photo counts roll into the cluster bubbles
                        // below the threshold; only render individual
                        // markers once the user has zoomed in.
                        <ZoomGated minZoom={CLUSTER_THRESHOLD_ZOOM}>
                          {photos.map((ph) => (
                            <PhotoMarker
                              key={ph.id}
                              photo={ph}
                              onSelect={() => {
                                setSelectedParcel(null);
                                setSelectedPhoto(ph);
                              }}
                            />
                          ))}
                        </ZoomGated>
                      ) : null}
                      {/* The legend explains the polygon and photo colours, so
                          only show it once those are on screen (past the
                          cluster-bubble zoom). Keeps the country view clean. */}
                      <ZoomGated minZoom={CLUSTER_THRESHOLD_ZOOM}>
                        <MapLegend />
                      </ZoomGated>
                    </MapContainer>
                  </SafeMap>
                  <IcasLoadingIndicator />
                  <PhotoPreviewPanel
                    photo={selectedPhoto}
                    // X fully closes (photo + any underlying polygon panel).
                    onClose={() => {
                      setSelectedPhoto(null);
                      setSelectedParcel(null);
                    }}
                    // When a polygon is still selected, the photo was opened
                    // from its panel: show a "back to polygon" affordance that
                    // just closes the photo and reveals the polygon info again.
                    onBack={
                      selectedParcel ? () => setSelectedPhoto(null) : undefined
                    }
                  />
                  <ParcelPhotosPanel
                    parcel={selectedParcel}
                    onClose={() => setSelectedParcel(null)}
                    // Keep the polygon selected so the photo opens as an overlay
                    // on top of its panel; closing the photo returns here.
                    onSelectPhoto={(ph) => {
                      setSelectedPhoto(ph);
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold text-slate-700">{t('database:filter.results')}</h2>
            </CardHeader>
            <CardBody className="p-0">
              {(() => {
                // Mirror the map layer toggles in the results table:
                //   - showPhotos off + showParcels off -> empty hint
                //   - showPhotos on   + showParcels off -> photos only
                //   - showPhotos off  + showParcels on  -> polygons only
                //   - both on -> photos first, then polygons (the user
                //     asked for photos-first because they typically
                //     drive the workflow; polygons are reference data
                //     they tap into for context).
                const visiblePhotos = showPhotos ? photos : [];
                const visibleParcels = showParcels ? parcels : [];
                const nothing =
                  visiblePhotos.length === 0 && visibleParcels.length === 0;
                if (nothing) {
                  return (
                    <div className="p-6 text-center text-sm text-slate-400">
                      {t('database:empty.filterAdjust')}
                    </div>
                  );
                }
                return (
                  <div className="max-h-80 overflow-auto">
                    {/* Photos section (first per user request) */}
                    {visiblePhotos.length > 0 ? (
                      <div>
                        <div className="sticky top-0 z-10 border-b border-blue-100 bg-blue-50/95 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-blue-700 backdrop-blur-sm dark:border-blue-900 dark:bg-blue-950/70 dark:text-blue-200">
                          {t('photos:filter.layerPhotos')}
                          <span className="ml-2 font-mono text-[10px] text-blue-500">
                            {visiblePhotos.length}
                          </span>
                        </div>
                        <table className="table-striped w-full text-sm dark:text-slate-300">
                          <thead className="bg-slate-50/95 text-xs uppercase text-slate-500 dark:bg-slate-800/95 dark:text-slate-400">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold">
                                {t('photos:table.header.fileName', {
                                  defaultValue: 'Fi\u0219ier',
                                })}
                              </th>
                              <th className="px-3 py-2 text-left font-semibold">
                                {t('database:table.header.raion')}
                              </th>
                              <th className="px-3 py-2 text-left font-semibold">
                                {t('photos:table.header.author', {
                                  defaultValue: 'Autor',
                                })}
                              </th>
                              <th className="px-3 py-2 text-right font-semibold">
                                {t('photos:table.header.year', {
                                  defaultValue: 'An',
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {visiblePhotos.map((ph) => (
                              <tr
                                key={ph.id}
                                className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-blue-50/60 dark:hover:bg-blue-950/40"
                                onClick={() => handlePhotoRowClick(ph)}
                                title={t('database:filter.zoomToPhoto', {
                                  defaultValue: 'Zoom to this photo on the map',
                                })}
                              >
                                <td className="max-w-[200px] truncate px-3 py-2 font-mono text-xs">
                                  {ph.fileName}
                                </td>
                                <td className="px-3 py-2">
                                  {ph.raion ?? '\u2014'}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-slate-600 dark:text-slate-400">
                                  {ph.author?.cod ?? ph.author?.fullName ?? '\u2014'}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                                  {ph.year ?? '\u2014'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    {/* Polygons section */}
                    {visibleParcels.length > 0 ? (
                      <div>
                        <div className="sticky top-0 z-10 border-b border-emerald-100 bg-emerald-50/95 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 backdrop-blur-sm dark:border-emerald-900 dark:bg-emerald-950/70 dark:text-emerald-200">
                          {t('photos:filter.layerParcels')}
                          <span className="ml-2 font-mono text-[10px] text-emerald-600">
                            {visibleParcels.length}
                          </span>
                        </div>
                        <table className="table-striped w-full text-sm dark:text-slate-300">
                          <thead className="bg-slate-50/95 text-xs uppercase text-slate-500 dark:bg-slate-800/95 dark:text-slate-400">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold">{t('database:table.header.raion')}</th>
                              <th className="px-3 py-2 text-left font-semibold">{t('database:table.header.primarie')}</th>
                              <th className="px-3 py-2 text-left font-semibold">{t('database:table.header.ocolSilvic')}</th>
                              <th className="px-3 py-2 text-left font-semibold">{t('database:table.header.ucf')}</th>
                              <th className="px-3 py-2 text-right font-semibold">{t('database:table.header.suprafata')}</th>
                              <th className="px-3 py-2 text-right font-semibold">{t('database:table.header.anul')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleParcels.map((r) => (
                              <tr
                                key={r.id}
                                className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-forest-50/60"
                                onClick={() => handleRowClick(r)}
                                title={t('database:filter.zoomToParcel', {
                                  defaultValue: 'Zoom to this parcel on the map',
                                })}
                              >
                                <td className="px-3 py-2">{r.raion}</td>
                                <td className="px-3 py-2">{r.primarie}</td>
                                <td className="px-3 py-2 text-slate-600">
                                  {r.ocolSilvic ?? '\u2014'}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs">{r.ucf}</td>
                                <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                                  {r.suprafata.toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                                  {r.anul}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                  </div>
                );
              })()}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PhotoMarker({ photo, onSelect }: { photo: PhotoRecordDto; onSelect: () => void }) {
  const style = useMapStyle();
  return (
    <CircleMarker
      center={[photo.latitude, photo.longitude]}
      radius={6}
      pathOptions={{
        color: style.photoRing ?? style.colors.photo,
        fillColor: style.colors.photo,
        fillOpacity: 0.8,
        weight: style.photoRing ? 2 : 1.5,
      }}
      // Shared canvas renderer (see PHOTO_CANVAS above) — all photo
      // markers paint to one canvas instead of one SVG node each.
      {...(PHOTO_CANVAS ? { renderer: PHOTO_CANVAS } : {})}
      eventHandlers={{
        click: (e) => {
          // Stop the click from bubbling up to the map-level handler.
          // Without this, the ParcelVectorTilesLayer's map.on('click')
          // also fires on the same event — when the photo is inside
          // (or near) a parcel, the parcel panel pops up alongside the
          // photo panel and clobbers the user's mental model of
          // "I tapped a photo, give me the photo."
          //
          // Leaflet's `_fireDOMEvent` checks `data.originalEvent._stopped`
          // before firing `map.on('click', ...)`, and `L.DomEvent.
          // stopPropagation` sets that flag ONLY when called with the
          // Leaflet event (its `else if (e.originalEvent)` branch);
          // calling it on the DOM event directly just does DOM
          // bubbling, which Leaflet doesn't observe internally. So we
          // pass the synthetic Leaflet event here. (Type-cast because
          // L.DomEvent.stopPropagation's signature expects an Event.)
          //
          // Rule after this:
          //   click ON a photo dot  -> photo preview only
          //   click on a polygon    -> polygon panel (with photos)
          L.DomEvent.stopPropagation(e as unknown as Event);
          onSelect();
        },
      }}
    />
  );
}
